import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Container, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Paper } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { createCampaignEpisode } from '../../services/campaignEpisodeService';
import { getAllJournalEntries } from '../../services/bennyJournalService'; // To populate the dropdown

const CreateCampaignEpisode = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [youtubeVideoUrl, setYoutubeVideoUrl] = useState('');
  const [description, setDescription] = useState('');
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [selectedBennyJournalId, setSelectedBennyJournalId] = useState('');
  const [actionItems, setActionItems] = useState(''); // Added state for action items
  
  const [bennyJournalEntries, setBennyJournalEntries] = useState([]);
  const [loadingJournalEntries, setLoadingJournalEntries] = useState(true);
  const [journalEntriesError, setJournalEntriesError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchJournalEntries = async () => {
      try {
        setLoadingJournalEntries(true);
        const entries = await getAllJournalEntries();
        // Assuming entries have a 'createdAt' field (Timestamp or ISO string) for sorting
        // Sort by newest first
        const sortedEntries = entries.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });
        setBennyJournalEntries(sortedEntries);
        setJournalEntriesError('');
      } catch (err) {
        console.error("Error fetching Benny's Journal entries:", err);
        setJournalEntriesError("Failed to load Benny's Journal entries. Please try again later.");
      } finally {
        setLoadingJournalEntries(false);
      }
    };

    fetchJournalEntries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!currentUser) {
      setError('You must be logged in to create an episode.');
      return;
    }

    setIsSubmitting(true);

    const episodeData = {
      title: title.trim(),
      youtubeVideoUrl: youtubeVideoUrl.trim() || null,
      description: description.trim() || null,
      episodeNumber: episodeNumber ? parseInt(episodeNumber, 10) : null,
      relatedBennyJournalId: selectedBennyJournalId || null,
      actionItems: actionItems || null, // Include action items
      createdBy: { // Optional: Add user info if needed
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
      },
      // createdAt will be handled by the service with serverTimestamp
    };

    try {
      const episodeId = await createCampaignEpisode(episodeData);
      setSuccess(`Campaign Episode created successfully! ID: ${episodeId}`);
      setTitle('');
      setYoutubeVideoUrl('');
      setDescription('');
      setEpisodeNumber('');
      setSelectedBennyJournalId('');
      setActionItems(''); // Clear action items
      // Optionally navigate to the new episode's page or a list page
      // navigate(`/campaign-episodes/${episodeId}`); 
      // or navigate('/campaign-episodes');
    } catch (err) {
      console.error("Error creating campaign episode:", err);
      setError(err.message || 'Failed to create campaign episode. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container component={Paper} maxWidth="md" sx={{ mt: 4, p: 3, backgroundColor: '#f5f5dc', border: '1px solid #d2b48c' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Georgia", serif', color: '#5c3c10' }}>
        Create New Campaign Episode
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Episode Title"
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            sx={{ backgroundColor: 'white' }}
          />
          <TextField
            label="YouTube Video URL (Optional)"
            variant="outlined"
            type="url"
            value={youtubeVideoUrl}
            onChange={(e) => setYoutubeVideoUrl(e.target.value)}
            fullWidth
            sx={{ backgroundColor: 'white' }}
          />
          <TextField
            label="Description (Optional)"
            variant="outlined"
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            sx={{ backgroundColor: 'white' }}
          />          <TextField
            label="Episode Number (Optional)"
            variant="outlined"
            type="number"
            value={episodeNumber}
            onChange={(e) => setEpisodeNumber(e.target.value)}
            fullWidth
            margin="normal"
            sx={{ backgroundColor: 'white' }}
          />          <TextField
            label="Action Items for Adventurers (Optional)"
            value={actionItems}
            onChange={(e) => setActionItems(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
            placeholder="E.g., - Investigate the strange noises from the old mill.\n- Purchase rations for a three-day journey."
            sx={{ backgroundColor: 'white' }}
          />          <FormControl fullWidth margin="normal" sx={{ backgroundColor: 'white' }}>
            <InputLabel id="benny-journal-select-label">Related Benny's Journal Entry (Optional)</InputLabel>
            <Select
              labelId="benny-journal-select-label"
              id="benny-journal-select"
              value={selectedBennyJournalId}
              onChange={(e) => setSelectedBennyJournalId(e.target.value)}
              label="Related Benny's Journal Entry (Optional)"
              disabled={loadingJournalEntries || !!journalEntriesError}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {loadingJournalEntries ? (
                <MenuItem value="" disabled>Loading entries...</MenuItem>
              ) : journalEntriesError ? (
                <MenuItem value="" disabled>{journalEntriesError}</MenuItem>
              ) : (
                bennyJournalEntries.map((entry) => (
                  <MenuItem key={entry.id} value={entry.id}>
                    {entry.title} ({(entry.createdAt?.toDate ? entry.createdAt.toDate() : new Date(entry.createdAt)).toLocaleDateString()})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {journalEntriesError && !loadingJournalEntries && (
             <Alert severity="warning" sx={{ mt: 1 }}>Could not load Benny's Journal entries for selection.</Alert>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ 
              mt: 2, 
              fontFamily: '"Georgia", serif',
              backgroundColor: '#8B4513', 
              color: '#F5F5F5',
              '&:hover': { backgroundColor: '#A0522D' },
              '&:disabled': { backgroundColor: 'grey' }
            }}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: '#F5F5F5' }} /> : 'Create Episode'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default CreateCampaignEpisode;
